import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { useMemo, useState, useEffect, useCallback, Fragment } from "react"
import { LoaderCircleIcon, PlusIcon, MessageSquareIcon, Trash2Icon, Edit2Icon, PencilIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData, useRevalidator } from "react-router"

import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { FiltersWrapper, type StoredSession } from "@/components/Filters"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { hasPermission } from "@/machines/Authmachine"

import type { RootLoader } from "@/routes/root"
import type { DocumentClassificationType, SubClassificationsType } from "@/utils/types"

const routeId = "brand-document-classification"
const sessionKey = "brand-document-classification-filter-storage"

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No Organisations Found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const loadedOrganisation = filters.raw?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedProject = filters.raw?.project ?? projects[0]

  const documents = await fetchAPI<DocumentClassificationType[]>(
    `/api/unityviewer/documentclassification/${loadedOrganisation}/${loadedProject}`,
  )
    .then(handleResponse)
    .catch(() => [])

  const groups = Array.from(new Set(documents.map((doc) => doc.name)))

  const selectedGroup =
    filters.raw?.group == null || !groups.includes(filters.raw.group) ? groups[0] : filters.raw.group

  return { loadedOrganisation, loadedProject, documents, projects, groups, selectedGroup }
}

async function action({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

  let errorMessage: string | undefined

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  switch (action) {
    case "CREATE_GROUP": {
      const groupName = ((formData.get("groupName") ?? "") as string).trim()
      const classificationName = ((formData.get("classificationName") ?? "") as string).trim()

      if (groupName === "") errorMessage = "Group Name is required"
      else if (classificationName === "") errorMessage = "Classification Name is required"

      if (errorMessage != null) break

      try {
        await fetchAPI(`/api/unityviewer/documentclassification/${organisation}/${project}`, {
          method: "POST",
          body: { group: groupName, classification: classificationName },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error creating group"
      }

      break
    }

    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

async function mainAction({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, organisation, project, groupName, classificationName } = Object.fromEntries(formData) as Record<
    string,
    string
  >

  let errorMessage: string | undefined

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  switch (action) {
    case "CREATE_CLASSIFICATION": {
      const selectedGroup = ((formData.get("selectedGroup") ?? "") as string).trim()
      const classificationName = ((formData.get("classificationName") ?? "") as string).trim()

      if (selectedGroup === "") errorMessage = "Please select a group first"
      else if (classificationName === "") errorMessage = "Classification Name is required"

      if (errorMessage != null) break

      try {
        await fetchAPI(`/api/unityviewer/documentclassification/${organisation}/${project}`, {
          method: "POST",
          body: { group: selectedGroup, classification: classificationName },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error creating classification"
      }

      break
    }

    case "DELETE_GROUP": {
      try {
        await fetchAPI(`/api/unityviewer/documentclassification/delete/${organisation}/${project}`, {
          method: "POST",
          body: { name: groupName },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Couldn't delete group"
      }
      break
    }

    case "DELETE_CLASSIFICATION": {
      try {
        await fetchAPI(`/api/unityviewer/documentclassification/delete/${organisation}/${project}/${groupName}`, {
          method: "POST",
          body: { name: classificationName },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Couldn't delete classification"
      }
      break
    }

    case "EDIT_CLASSIFICATION": {
      const newName = (formData.get("newName") as string).trim()

      if (newName !== classificationName.trim()) {
        try {
          await fetchAPI(`/api/unityviewer/documentclassification/${organisation}/${classificationName}`, {
            method: "PATCH",
            body: { newName },
          }).then(throwIfNotOk)
        } catch (error) {
          console.error(error)
          errorMessage = "Error updating classification"
        }
      }
      break
    }
    case "EDIT_GROUPNAME": {
      const newName = (formData.get("newName") as string).trim()

      if (newName !== groupName.trim()) {
        try {
          await fetchAPI(`/api/unityviewer/documentclassification/${organisation}/${groupName}`, {
            method: "PATCH",
            body: { newName },
          }).then(throwIfNotOk)
        } catch (error) {
          console.error(error)
          errorMessage = "Error updating classification"
        }
      }
      break
    }

    case "ANNOTATE_GROUP": {
      const annotation = (formData.get("annotation") as string).trim()

      try {
        await fetchAPI(`/api/unityviewer/documentclassification/annotate/${organisation}/${project}`, {
          method: "PATCH",
          body: { name: groupName, annotation: annotation },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error updating group annotation"
      }
      break
    }

    case "ANNOTATE_CLASSIFICATION": {
      const annotation = (formData.get("annotation") as string).trim()

      try {
        await fetchAPI(`/api/unityviewer/documentclassification/annotate/${organisation}/${project}/${groupName}`, {
          method: "PATCH",
          body: { name: classificationName, annotation: annotation },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error updating classification annotation"
      }
      break
    }

    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

export function BrandDocumentClassification() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)

  const onBeforeSubmit = useCallback(
    (formData: FormData, reason: { type: "field"; name: string } | { type: "submitButton" }) => {
      if (reason.type !== "field") return
      if (reason.name === "organisation") formData.delete("project")
      if (reason.name === "organisation" || reason.name === "project") formData.delete("group")
    },
    [],
  )

  return (
    <Fragment>
      {rootData != null && data != null && (
        <div className="flex items-center gap-4">
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            {rootData.organisations.length > 1 && (
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={data.loadedOrganisation}
                items={rootData.organisations}
              />
            )}
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
            {data.groups.length > 0 && (
              <FiltersWrapper.Select name="group" label="Group" defaultValue={data.selectedGroup} items={data.groups} />
            )}
          </FiltersWrapper>
          <div className="flex-1" />
          <DocumentClassificationAddNewGroup />
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()

  const selectedGroupData = useMemo(
    () => data.documents?.find((doc) => doc.name === data.selectedGroup),
    [data.documents, data.selectedGroup],
  )
  const classificationsForGroup = selectedGroupData?.subClassifications ?? []
  const isGroupInUse = selectedGroupData?.isInUse ?? false

  return (
    <div className="h-full overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-80">Group Name</TableHead>
            <TableHead>Annotation</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {selectedGroupData != null ? (
            <TableRow>
              <TableCell className="w-80">
                {selectedGroupData.name}
                {isGroupInUse && (
                  <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                    In Use
                  </span>
                )}
              </TableCell>
              <TableCell className="text-gray-600">{selectedGroupData.annotation}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <DocumentClassificationAnnotateGroup selectedGroupData={selectedGroupData} />
                  <DocumentClassificationEditGroupName />
                  <DocumentClassificationAddNew />
                  <DocumentClassificationDeleteGroup />
                </div>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="py-8 text-center text-gray-500">
                No Group found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-80">Classification Name</TableHead>
            <TableHead>Annotation</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {classificationsForGroup.length > 0 ? (
            classificationsForGroup.map((classification) => (
              <TableRow key={classification.name}>
                <TableCell className="w-80">
                  {classification.name}
                  {classification.isInUse === true && (
                    <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                      In Use
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-gray-600">{classification.annotation}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <DocumentClassificationAnnotateItem classification={classification} />
                    <DocumentClassificationEditItem classification={classification} />
                    <DocumentClassificationDeleteItem classification={classification} />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="py-8 text-center text-gray-500">
                No classifications found in this group
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Outlet />
    </div>
  )
}

function DocumentClassificationAddNewGroup() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [open, setOpen] = useState(false)

  const closeDialog = useCallback(() => setOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New Group
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Group</DialogTitle>
        </DialogHeader>
        <DocumentClassificationAddNewGroupForm closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentClassificationAddNewGroupForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)
  const revalidator = useRevalidator()
  const [createAnother, setCreateAnother] = useState(false)
  const [formKey, setFormKey] = useState<string>()

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.action !== "CREATE_GROUP" || fetcher.data?.errorMessage != null)
      return
    setTimeout(() => {
      revalidator.revalidate()
    }, 50)

    if (createAnother) setFormKey(Math.random().toString(36))
    else closeDialog()
  }, [closeDialog, createAnother, fetcher.data?.action, fetcher.data?.errorMessage, fetcher.state])

  if (data == null) return null

  const errorMessage = fetcher.data?.errorMessage

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="CREATE_GROUP" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div>
        <label className="text-sm font-medium">Group Name</label>
        <Input key={formKey} required name="groupName" placeholder="Enter group name" autoFocus />
      </div>

      <div>
        <label className="text-sm font-medium">Initial Classification Name</label>
        <Input key={formKey} required name="classificationName" placeholder="Enter first classification name" />
      </div>

      <div className="flex items-center space-x-2 border-t pt-2">
        <input
          type="checkbox"
          id="createAnotherGroup"
          checked={createAnother}
          onChange={(e) => setCreateAnother(e.target.checked)}
          className="rounded border border-input"
        />
        <label
          htmlFor="createAnotherGroup"
          className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Create another?
        </label>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Create Group</Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationEditGroupName() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  const onSuccess = useCallback(() => setIsOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
              <PencilIcon size={24} color="black" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Edit Group Name</TooltipContent>
      </Tooltip>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Group Name </DialogTitle>
        </DialogHeader>
        <DocumentGroupEditForm onSuccess={onSuccess} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentGroupEditForm({ onSuccess }: { onSuccess: () => void }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [newName, setNewName] = useState(data?.selectedGroup ?? "")

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.data, fetcher.state, onSuccess])

  if (data == null) return null

  const errorMessage = fetcher.data?.errorMessage

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="EDIT_GROUPNAME" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="groupName" value={data.selectedGroup} />
      <div>
        <label className="text-sm font-medium">Name</label>
        <Input
          name="newName"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter name"
          autoFocus
        />
      </div>
      <p className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <span className="font-bold">Warning:</span> This will rename this group for all brands in this client.
      </p>
      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Update</Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationAddNew() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  const closeDialog = useCallback(() => setIsOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost">
              <PlusIcon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Add New Classification</TooltipContent>
      </Tooltip>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Classification to {data.selectedGroup}</DialogTitle>
        </DialogHeader>
        <DocumentClassificationAddForm closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentClassificationAddForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [createAnother, setCreateAnother] = useState(false)
  const [formKey, setFormKey] = useState<string>()

  useEffect(() => {
    if (
      fetcher.state !== "loading" ||
      fetcher.data?.action !== "CREATE_CLASSIFICATION" ||
      fetcher.data?.errorMessage != null
    )
      return

    if (createAnother) setFormKey(Math.random().toString(36))
    else closeDialog()
  }, [fetcher.state, fetcher.data?.action, fetcher.data?.errorMessage, createAnother, closeDialog])

  if (data == null) return null

  const errorMessage = fetcher.data?.errorMessage

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="CREATE_CLASSIFICATION" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="selectedGroup" value={data.selectedGroup} />
      <div>
        <label className="text-sm font-medium">Classification Name</label>
        <Input key={formKey} required name="classificationName" placeholder="Enter classification name" autoFocus />
      </div>
      <div className="flex items-center space-x-2 border-t pt-2">
        <input
          type="checkbox"
          id="createAnotherClassification"
          checked={createAnother}
          onChange={(e) => setCreateAnother(e.target.checked)}
          className="rounded border border-input"
        />
        <label
          htmlFor="createAnotherClassification"
          className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Create another?
        </label>
      </div>
      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Add Classification</Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationAnnotateGroup({
  selectedGroupData,
}: {
  selectedGroupData?: DocumentClassificationType
}) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  const onSuccess = useCallback(() => setIsOpen(false), [])

  if (data == null || !hasPermission("writeAccess") || selectedGroupData == null) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="disabled:cursor-not-allowed">
              <MessageSquareIcon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Annotate Group</TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Annotate Group: {data.selectedGroup}</DialogTitle>
        </DialogHeader>
        <DocumentClassificationAnnotateGroupForm selectedGroupData={selectedGroupData} onSuccess={onSuccess} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentClassificationAnnotateGroupForm({
  selectedGroupData,
  onSuccess,
}: {
  selectedGroupData: DocumentClassificationType
  onSuccess: () => void
}) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.data, fetcher.state, onSuccess])

  if (data == null) return null

  const errorMessage = fetcher.data?.errorMessage

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="ANNOTATE_GROUP" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="groupName" value={data.selectedGroup} />

      <div>
        <label className="text-sm font-medium">Group Annotation</label>
        <textarea
          name="annotation"
          defaultValue={selectedGroupData.annotation}
          placeholder="Enter description or notes for this group"
          rows={4}
          autoFocus
          className="w-full rounded-md border p-2"
        />
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Update Group Annotation</Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationDeleteGroup() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  const selectedGroupData = useMemo(() => {
    return data?.documents?.find((doc) => doc.name === data.selectedGroup)
  }, [data?.documents, data?.selectedGroup])

  const isGroupInUse = selectedGroupData?.isInUse ?? false

  if (data == null || !hasPermission("deleteAccess")) return null

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              disabled={isGroupInUse}
              className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
            >
              <Trash2Icon />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Group</TooltipContent>
      </Tooltip>
      <PopoverContent align="end">
        <p className="text-sm text-pretty">
          Are you sure you want to delete the group &quot;<span className="font-semibold">{data.selectedGroup}</span>
          &quot;?
          <span className="mt-1 block text-red-600">This will also delete all classifications in this group.</span>
        </p>
        <DocumentClassificationDeleteGroupForm />
      </PopoverContent>
    </Popover>
  )
}

function DocumentClassificationDeleteGroupForm() {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE_GROUP" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="groupName" value={data.selectedGroup} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        <p className="text-sm text-destructive empty:hidden">{fetcher.data?.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm Delete
      </Button>
    </fetcher.Form>
  )
}

function DocumentClassificationEditItem({ classification }: { classification: SubClassificationsType }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  const onSuccess = useCallback(() => setIsOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost">
              <Edit2Icon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Edit Classification</TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Classification</DialogTitle>
        </DialogHeader>
        <DocumentClassificationEditForm classification={classification} onSuccess={onSuccess} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentClassificationEditForm({
  classification,
  onSuccess,
}: {
  classification: SubClassificationsType
  onSuccess: () => void
}) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [newName, setNewName] = useState(classification.name)

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.data, fetcher.state, onSuccess])

  if (data == null) return null

  const errorMessage = fetcher.data?.errorMessage

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="EDIT_CLASSIFICATION" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="groupName" value={data.selectedGroup} />
      <input type="hidden" name="classificationName" value={classification.name} />
      <div>
        <label className="text-sm font-medium">Name</label>
        <Input
          name="newName"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter name"
          autoFocus
        />
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm text-amber-800">
          <strong>Warning:</strong> This will rename the classification for all brands in this client.
        </p>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Update</Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationAnnotateItem({ classification }: { classification: SubClassificationsType }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  const onSuccess = useCallback(() => setIsOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost">
              <MessageSquareIcon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Add Annotation</TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Annotate Classification</DialogTitle>
        </DialogHeader>
        <DocumentClassificationAnnotateForm classification={classification} onSuccess={onSuccess} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentClassificationAnnotateForm({
  classification,
  onSuccess,
}: {
  classification: SubClassificationsType
  onSuccess: () => void
}) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.data, fetcher.state, onSuccess])

  if (data == null) return null

  const errorMessage = fetcher.data?.errorMessage

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="ANNOTATE_CLASSIFICATION" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="groupName" value={data.selectedGroup} />
      <input type="hidden" name="classificationName" value={classification.name} />

      <div>
        <label className="text-sm font-medium">Annotation</label>
        <textarea
          name="annotation"
          defaultValue={classification.annotation}
          placeholder="Enter description or notes"
          rows={4}
          autoFocus
          className="w-full rounded-md border p-2"
        />
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
          </span>
        ) : (
          <p className="text-sm text-destructive">{errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Update Annotation</Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationDeleteItem({ classification }: { classification: SubClassificationsType }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  if (data == null || !hasPermission("deleteAccess")) return null

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              disabled={classification.isInUse}
              className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
            >
              <Trash2Icon />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Classification</TooltipContent>
      </Tooltip>
      <PopoverContent align="end">
        <p className="text-sm text-pretty">
          Are you sure you want to delete the classification &quot;
          <span className="font-semibold">{classification.name}</span>&quot;?
        </p>
        <DocumentClassificationDeleteItemForm classification={classification} />
      </PopoverContent>
    </Popover>
  )
}

function DocumentClassificationDeleteItemForm({ classification }: { classification: SubClassificationsType }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE_CLASSIFICATION" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="groupName" value={data.selectedGroup} />
      <input type="hidden" name="classificationName" value={classification.name} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        <p className="text-sm text-destructive empty:hidden">{fetcher.data?.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm Delete
      </Button>
    </fetcher.Form>
  )
}

BrandDocumentClassification.Main = Main
BrandDocumentClassification.loader = loader
BrandDocumentClassification.action = action
BrandDocumentClassification.routeId = routeId

Main.action = mainAction

export interface Project {
  organisation: string
  name: string
  active: boolean
}
